/**
 * Owner-layer KB writes — refactored to design-system v1.0.
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/05-flows.md § 11.3
 *
 * Full CRUD: list + add + edit + delete. Edit re-uses the same write
 * endpoint (it supersedes the prior entry by contentKey + language).
 * Delete is a soft-delete (sets supersededAt). The voice agent reads the
 * cascade resolver which automatically reflects supersedes + deletes.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '@/components/kol/Icon';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import { Button, Card, BottomSheet, Input } from '@/components/ui';
import { ApiError, deleteVehicleKb, listVehicleKb, writeVehicleKb, type KBOverrideRow } from '@/lib/api';

type WritableLayer = 'OWNER' | 'VEHICLE';

export function OwnerKbPage() {
  const { t, i18n } = useTranslation();
  const { id: vehicleId } = useParams<{ id: string }>();
  const [entries, setEntries] = useState<KBOverrideRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<KBOverrideRow | null>(null);

  const defaultLang = (i18n.language === 'en' ? 'en' : i18n.language === 'ar' ? 'ar' : 'he') as 'he' | 'en' | 'ar';

  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await listVehicleKb(vehicleId);
        if (!cancelled) setEntries(data.items);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId, refreshKey, t]);

  if (!vehicleId) return null;

  function openAdd() {
    setEditing(null);
    setSheetOpen(true);
  }
  function openEdit(entry: KBOverrideRow) {
    setEditing(entry);
    setSheetOpen(true);
  }

  return (
    <Layout
      title={t('kb.title')}
      trailing={
        <Link
          to={`/kol/vehicles/${vehicleId}`}
          aria-label={t('common.back', { defaultValue: 'חזרה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="arrow" size={19} />
        </Link>
      }
    >
      <Card variant="nested" className="mb-4">
        <p className="text-body-sm text-carnaf-ink-muted leading-relaxed">{t('kb.intro')}</p>
      </Card>

      {error && (
        <p role="alert" className="text-body-sm text-carnaf-red mb-4">
          {error}
        </p>
      )}

      {entries === null && !error && <p className="text-carnaf-ink-muted mt-6">{t('common.loading')}</p>}

      {entries && entries.length === 0 && <p className="text-carnaf-ink-muted mt-6">{t('kb.empty')}</p>}

      {entries && entries.length > 0 && (
        <ul className="space-y-2 pb-24">
          {entries.map((e) => (
            <EntryRow key={e.id} entry={e} onEdit={() => openEdit(e)} />
          ))}
        </ul>
      )}

      <div
        className="fixed bottom-0 inset-x-0 bg-white border-t border-carnaf-line px-5 py-4"
        style={{
          paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          boxShadow: '0 -1px 0 0 #E0E8E1, 0 -8px 24px -8px rgba(26, 30, 29, 0.06)',
        }}
      >
        <Button variant="primary" size="md" fullWidth onClick={openAdd}>
          + {t('kb.add_title')}
        </Button>
      </div>

      <KbEntrySheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        vehicleId={vehicleId}
        defaultLanguage={defaultLang}
        editing={editing}
        onSaved={() => {
          setSheetOpen(false);
          setRefreshKey((n) => n + 1);
        }}
        onDeleted={() => {
          setSheetOpen(false);
          setRefreshKey((n) => n + 1);
        }}
      />
    </Layout>
  );
}

/**
 * Render a stored KB value (typed `unknown`) human-readably — never a raw JSON
 * blob. Prefers `.text`; for other objects renders "label: value" lines; falls
 * back to a localized "unreadable" placeholder rather than a serialized object.
 */
function readableContent(value: unknown, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.text === 'string' && obj.text.trim()) return obj.text;
    const lines = Object.entries(obj)
      .filter(([, v]) => v != null && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'))
      .map(([k, v]) => `${prettyKey(k)}: ${String(v)}`);
    if (lines.length) return lines.join(' · ');
  }
  return t('kb.unreadable', { defaultValue: 'תוכן לא קריא' });
}

function EntryRow({ entry, onEdit }: { entry: KBOverrideRow; onEdit: () => void }): JSX.Element {
  const { t } = useTranslation();
  const text = readableContent(entry.contentValue, t);
  return (
    <li>
      <button
        type="button"
        onClick={onEdit}
        className="w-full text-start bg-white border border-carnaf-line rounded-md px-4 py-3 flex items-start gap-3 hover:bg-carnaf-paper-elevated transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[15px] text-carnaf-ink-muted mb-0.5">{prettyKey(entry.contentKey)}</div>
          {/* line-clamp (not truncate): an LTR value inside this RTL container
              gets its START eaten by single-line truncate ("…e: Avi Garage").
              Clamping to two lines preserves the meaningful prefix. */}
          <div className="text-body text-carnaf-ink line-clamp-2">
            <bdi>{text}</bdi>
          </div>
          <div className="text-[15px] text-carnaf-ink-faint mt-1">
            {t(`kb.layers.${entry.layer.toLowerCase()}`)} · {entry.language}
          </div>
        </div>
        <span className="text-carnaf-ink-faint" aria-hidden>
          <Icon name="edit" size={18} />
        </span>
      </button>
    </li>
  );
}

function prettyKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function KbEntrySheet({
  open,
  onClose,
  vehicleId,
  defaultLanguage,
  editing,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  vehicleId: string;
  defaultLanguage: 'he' | 'en' | 'ar';
  editing: KBOverrideRow | null;
  onSaved: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [layer, setLayer] = useState<WritableLayer>('OWNER');
  const [contentKey, setContentKey] = useState('');
  const [contentText, setContentText] = useState('');
  const [language, setLanguage] = useState<'he' | 'en' | 'ar'>(defaultLanguage);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync form with editing entry when sheet opens
  useEffect(() => {
    if (open) {
      if (editing) {
        setLayer(editing.layer === 'VEHICLE' ? 'VEHICLE' : 'OWNER');
        setContentKey(editing.contentKey);
        const text =
          typeof editing.contentValue === 'object' && editing.contentValue !== null
            ? ((editing.contentValue as { text?: string }).text ?? readableContent(editing.contentValue, t))
            : String(editing.contentValue ?? '');
        setContentText(text);
        setLanguage((editing.language as 'he' | 'en' | 'ar') ?? defaultLanguage);
      } else {
        setLayer('OWNER');
        setContentKey('');
        setContentText('');
        setLanguage(defaultLanguage);
      }
      setError(null);
    }
  }, [open, editing, defaultLanguage]);

  async function onSubmit() {
    setError(null);
    setPending(true);
    try {
      await writeVehicleKb(vehicleId, {
        layer,
        contentKey: contentKey.trim(),
        contentValue: { text: contentText.trim() },
        language,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onDelete() {
    if (!editing) return;
    setError(null);
    setPending(true);
    try {
      await deleteVehicleKb(vehicleId, editing.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setPending(false);
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={editing ? t('kb.edit_title', { defaultValue: 'עריכת מידע' }) : t('kb.add_title')}
      footer={
        <Button
          variant="primary"
          size="md"
          fullWidth
          onClick={onSubmit}
          disabled={pending || !contentKey.trim() || !contentText.trim()}
        >
          {pending ? t('common.loading') : t('common.save')}
        </Button>
      }
    >
      <div className="space-y-4">
        <Input
          name="contentKey"
          label={t('kb.key_label')}
          helper={t('kb.key_helper', { defaultValue: 'מזהה יציב לסוכן. אנגלית בלבד.' })}
          placeholder={t('kb.key_placeholder') ?? ''}
          value={contentKey}
          onChange={(e) => setContentKey(e.target.value)}
          dir="ltr"
          maxLength={80}
          disabled={!!editing /* key is the identity for supersede; don't allow changing */}
        />

        <div>
          <label className="block text-body-sm font-medium text-carnaf-ink-muted mb-1.5">{t('kb.value_label')}</label>
          <textarea
            value={contentText}
            onChange={(e) => setContentText(e.target.value)}
            placeholder={t('kb.value_placeholder') ?? ''}
            rows={4}
            maxLength={2000}
            className="w-full px-4 py-3 rounded-md border border-carnaf-line bg-white text-body focus:outline-none focus:ring-2 focus:ring-carnaf-teal-soft focus:border-carnaf-teal resize-none"
            style={{ minHeight: 88 }}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-body-sm font-medium text-carnaf-ink-muted mb-1.5">
              {t('kb.language_label')}
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'he' | 'en' | 'ar')}
              className="w-full px-3 py-2.5 rounded-md border border-carnaf-line bg-white text-body"
              style={{ minHeight: 44 }}
            >
              <option value="he">עברית</option>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </div>
          <div>
            <label className="block text-body-sm font-medium text-carnaf-ink-muted mb-1.5">{t('kb.layer_label')}</label>
            <select
              value={layer}
              onChange={(e) => setLayer(e.target.value as WritableLayer)}
              className="w-full px-3 py-2.5 rounded-md border border-carnaf-line bg-white text-body"
              style={{ minHeight: 44 }}
            >
              <option value="VEHICLE">{t('kb.layers.vehicle')}</option>
              <option value="OWNER">{t('kb.layers.owner')}</option>
            </select>
          </div>
        </div>

        {error && (
          <p role="alert" className="text-body-sm text-carnaf-red">
            {error}
          </p>
        )}

        {editing && (
          <div className="pt-3 border-t border-carnaf-line text-center">
            <button
              type="button"
              onClick={onDelete}
              disabled={pending}
              className="inline-flex items-center justify-center gap-1.5 text-body-sm font-medium text-carnaf-red disabled:opacity-50"
              style={{ minHeight: 40 }}
            >
              <Icon name="trash" size={16} />
              {t('kb.delete', { defaultValue: 'מחיקת המידע' })}
            </button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
